<script setup lang="ts">
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useRoute, useRouter } from 'vue-router'

const auth = useAuthStore()
const ui = useUiStore()
const route = useRoute()
const router = useRouter()

// O banner Android é uma feature secundária. Mantê-lo assíncrono impede que
// seus ícones, estilos e lógica de HEAD do APK entrem no primeiro bundle.
const AppInstallBanner = defineAsyncComponent(() => import('@/components/layout/AppInstallBanner.vue'))
const showInstallBanner = ref(false)

function isAndroidBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const isAndroid = /android/i.test(navigator.userAgent || '')
  const isEmbedded = /wv|electron|headless/i.test(navigator.userAgent || '')
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false
  return isAndroid && !isEmbedded && coarsePointer
}

onMounted(() => {
  // O useDark mantém a preferência persistida; esta chamada apenas garante o
  // estado visual antes da primeira interação.
  document.documentElement.classList.toggle('dark', ui.isDark)
  showInstallBanner.value = isAndroidBrowser()
})

// Realtime só é necessário depois que uma sessão autenticada entra no painel.
// O import dinâmico evita puxar conexões, ofertas, notificações e filas para a
// landing pública.
let prevAuth = false
let prevCompanyId: string | null = null

async function connectRealtime() {
  const { useRealtimeStore } = await import('@/stores/realtime')
  useRealtimeStore().connect()
}

async function disconnectRealtime() {
  const { useRealtimeStore } = await import('@/stores/realtime')
  useRealtimeStore().disconnect()
}

watch(
  () => [auth.isAuthenticated, auth.currentCompanyId] as const,
  ([isAuth, companyId]) => {
    const becameAuth = isAuth && Boolean(companyId) && (!prevAuth || !prevCompanyId)
    const becameAnon = (!isAuth || !companyId) && (prevAuth || Boolean(prevCompanyId))
    if (becameAuth) void connectRealtime()
    if (becameAnon) void disconnectRealtime()
    prevAuth = isAuth
    prevCompanyId = companyId
  },
  { immediate: true },
)

// A sessão pode expirar sem navegação. Reage imediatamente à limpeza feita
// pelo client HTTP, desmonta o painel e evita qualquer usuário fictício.
watch(() => auth.isAuthenticated, (authenticated, wasAuthenticated) => {
  if (!authenticated && wasAuthenticated && route.meta.requiresAuth) {
    void router.replace({ name: 'login', query: { redirect: route.fullPath } })
  }
})
</script>

<template>
  <!-- Rotas públicas aparecem assim que o router resolve o componente; não há
       splash artificial nem pausa fixa antes da landing. -->
  <RouterView v-slot="{ Component, route }">
    <component :is="Component" :key="route.path" />
  </RouterView>

  <component v-if="showInstallBanner" :is="AppInstallBanner" />
</template>
